import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { Helpers } from 'src/app/models/helpers';
import { IAccount } from 'src/app/models/IAccount.model';
import { IWsResult } from 'src/app/models/ws_result.model';
import { AccountsService } from 'src/app/services/accounts.service';
import { AuthService } from 'src/app/services/auth.service';
import { Log } from 'src/app/services/shared/log';
import {
  SortDirection,
  nextSortState,
  sortItems,
} from 'src/app/utils/table-sort';

/**
 * Modal mode driven by `modalMode`.
 *   - `null`     no modal open, the table is the only thing visible
 *   - `view`     read-only details panel for `selectedAccount`
 *   - `new`      empty form, save creates a new account
 *   - `edit`     form prefilled with `selectedAccount`, save updates
 *   - `delete`   confirm dialog with the account name + a destructive
 *                button
 */
type AccountModalMode = 'view' | 'new' | 'edit' | 'delete' | null;

/**
 * Display descriptor for a role badge in the table.
 * Built from `ac_user_type_cod` via `roleBadge()`.
 */
interface RoleBadge {
  /** Italian label rendered inside the pill. */
  label: string;
  /** CSS class suffix — used as `role-badge--<value>` in the SCSS. */
  variant:
    | 'super-admin'
    | 'admin'
    | 'user'
    | 'screen'
    | 'manufacturer'
    | 'agent'
    | 'none';
}

/**
 * Gestione Account — single-page list with modals.
 *
 * Replaces the four legacy components that used to live under
 * `gestione-account/*` (list / dettagli / new / edit) plus their
 * separate `/accounts/:id`, `/new-account` routes. The page now lives
 * at the single route `/accounts` and uses in-component modals for
 * Create, Edit, View and Delete confirmation.
 *
 * Audience: Admin + SuperAdmin (see route guard). Typical population
 * is on the order of 50–200 accounts, so we do all the filtering
 * (search + role) and the pagination client-side — no extra HTTP
 * round-trips when the user types.
 *
 * Design language matches dashboard / statistics / settings /
 * messaggi: Diakonix blue palette, sticky page header, role badges
 * colour-coded per `Helpers.Permision*`, avatar initials coherent
 * with the chat sidebar.
 *
 * Architectural notes / future work:
 *  - Avatar upload (`saveAvatar`) is intentionally not wired here.
 *    It can land as a follow-up "edit avatar" action from the View
 *    modal, opening a separate upload widget. We didn't want a
 *    half-baked file-picker in the form on day one.
 *  - The role list is hard-coded from `Helpers.Permision*`. If the
 *    backend ever exposes a role-catalogue endpoint, swap the static
 *    list for that call.
 *  - `ChangeDetectorRef.markForCheck()` is sprinkled in the
 *    subscribe callbacks for the same reason as ChatComponent —
 *    HTTP responses on this codebase don't reliably re-enter the
 *    Angular zone for CD on this route family. Cheap to add, fixes
 *    the "stale UI after save" symptom that the legacy component
 *    masked with explicit `detectChanges()`.
 */
@Component({
  selector: 'app-account-list',
  templateUrl: './gestione-account-list.component.html',
  styleUrls: ['./gestione-account-list.component.scss'],
  standalone: false,
})
export class GestioneAccountListComponent implements OnInit {
  private readonly accountsService = inject(AccountsService);
  // AuthService exposes the password-reset endpoint (`/reset_password/`).
  // We expose it as an action in the Edit modal so an admin can trigger
  // the reset flow for another account without giving them the ability
  // to set a plaintext password from this form. See `triggerPasswordReset`.
  private readonly authService = inject(AuthService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  // v2.14.2: query-param deep link. La sidebar (click sul nome utente)
  // naviga a `/accounts?openAccountId=<id>`. Al primo load leggiamo il
  // param, apriamo il modale "view" per l'account matchato e ripuliamo
  // l'URL così un refresh non ri-apre il modale (comportamento UX
  // atteso: il param è "istruzione one-shot", non stato persistente).
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);

  public sortColumn: string | null = null;
  public sortDirection: SortDirection = 'asc';
  public filterTerm = '';
  public selectedAccountt: string | null = null;

  /** Page size for client-side pagination. */
  static readonly PAGE_SIZE = 20;

  // ── Data ──────────────────────────────────────────────────────────
  /** All accounts loaded from the backend at boot + after every mutation. */
  accounts: IAccount[] = [];

  // ── UI state ──────────────────────────────────────────────────────
  searchText = '';
  /** `null` = all roles; otherwise the `ac_user_type_cod` to keep. */
  roleFilter: IAccount['ac_user_type_cod'] | null = null;
  currentPage = 0;
  sortField = '';
  sortDir: 'asc' | 'desc' = 'asc';

  // ── Modal state ───────────────────────────────────────────────────
  modalMode: AccountModalMode = null;
  selectedAccount: IAccount | null = null;
  accountForm!: FormGroup;

  // ── Async flags ───────────────────────────────────────────────────
  loading = false;
  saving = false;
  deleting = false;
  resettingPassword = false;

  /**
   * Role catalogue rendered in the dropdown filter and in the form
   * "ruolo" select. Order is meaningful (most-privileged → least).
   * Bound to `IAccount.ac_user_type_cod`. `AGENT` is intentionally
   * left out of the *create* form because agent accounts are
   * provisioned through a different flow (mobile app onboarding);
   * keeping it in the *filter* though, so we can find existing ones.
   */
  readonly roles: ReadonlyArray<{
    code: IAccount['ac_user_type_cod'];
    label: string;
    creatable: boolean;
  }> = [
    { code: 0, label: 'Super Admin', creatable: true },
    { code: 1, label: 'Admin', creatable: true },
    { code: 2, label: 'Operatore', creatable: true },
    { code: 3, label: 'Schermo TV', creatable: true },
  ];

  ngOnInit(): void {
    this.accountForm = this.buildForm();
    this.loadAccounts();
  }

  // ──────────────────────────────────────────────────────────────────
  // Derived state (getters — keeps the template declarative).
  // ──────────────────────────────────────────────────────────────────

  /**
   * Accounts after applying:
   *   • permanent exclusion of manufacturer accounts (have their own
   *     dedicated page at `/fornitore-accounts`)
   *   • optional roleFilter (chip-style)
   *   • searchText
   *
   * 🚨 FIX v2.12.1 (R.1): pre-v2.12.1 questa pagina mostrava TUTTI gli
   * account incluso i fornitori (ac_user_type_cod === PermisionManufacturer)
   * che hanno una pagina dedicata `/fornitore-accounts`. Sembrava un
   * duplicato inutile. Aggiunto filtro permanente che esclude i
   * manufacturer dal grid Diakonix. Il filtro NON è togglable: gli
   * admin che vogliono gestire fornitori vanno sulla loro pagina.
   */
  get filteredAccounts(): IAccount[] {
    const q = this.searchText.trim().toLowerCase();
    return this.accounts.filter((acc) => {
      if (acc.ac_user_type_cod === Helpers.PermisionManufacturer) {
        return false;
      }
      if (
        this.roleFilter !== null &&
        acc.ac_user_type_cod !== this.roleFilter
      ) {
        return false;
      }
      if (!q) return true;
      return this.haystack(acc).includes(q);
    });
  }

  /** Slice of `filteredAccounts` visible on the current page. */
  get pagedAccounts(): IAccount[] {
    const start = this.currentPage * GestioneAccountListComponent.PAGE_SIZE;
    const sorted = this.sortField
      ? [...this.filteredAccounts].sort((a, b) => {
          const rawA = (a as any)[this.sortField] ?? '';
          const rawB = (b as any)[this.sortField] ?? '';
          const va = rawA !== '' && !isNaN(Number(rawA)) ? Number(rawA) : rawA;
          const vb = rawB !== '' && !isNaN(Number(rawB)) ? Number(rawB) : rawB;
          const cmp = va < vb ? -1 : va > vb ? 1 : 0;
          return this.sortDir === 'asc' ? cmp : -cmp;
        })
      : this.filteredAccounts;
    return sorted.slice(start, start + GestioneAccountListComponent.PAGE_SIZE);
  }

  /** Total number of pages (>= 1 to avoid "page 0 of 0" weirdness). */
  get totalPages(): number {
    return Math.max(
      1,
      Math.ceil(
        this.filteredAccounts.length / GestioneAccountListComponent.PAGE_SIZE,
      ),
    );
  }

  /** Convenience flag for the empty-state template branch. */
  get hasAccounts(): boolean {
    return this.accounts.length > 0;
  }

  // ──────────────────────────────────────────────────────────────────
  // Template helpers.
  // ──────────────────────────────────────────────────────────────────

  /** Two-character initials (e.g. "Mario Rossi" → "MR"). */
  initials(account: IAccount): string {
    const first = (account.ac_first_name || '').trim();
    const last = (account.ac_last_name || '').trim();
    const a = first ? first[0] : '';
    const b = last ? last[0] : '';
    return (a + b).toUpperCase() || '?';
  }

  /** Pretty role badge for the table cell. */
  roleBadge(account: IAccount): RoleBadge {
    return this.badgeFromCode(account.ac_user_type_cod);
  }

  /**
   * Backend uses 'S' / 'N' for the active flag, but `ac_active` is
   * declared optional on `IAccount` and historically not populated for
   * every record. `=== 'S'` would mark any legacy/unset record as
   * inactive purely because of missing data; the safer default for the
   * UI is "active unless explicitly marked 'N'".
   */
  isActive(account: IAccount): boolean {
    return account.ac_active !== 'N';
  }

  /** ISO/Date → `dd/MM/yyyy` for the expiration column. */
  formatDate(value: string | Date | null | undefined): string {
    if (!value) return '—';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  // ──────────────────────────────────────────────────────────────────
  // Actions — table.
  // ──────────────────────────────────────────────────────────────────

  sort(field: string): void {
    if (this.sortField === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDir = 'asc';
    }
    this.currentPage = 0;
  }

  sortIcon(field: string): string {
    if (this.sortField !== field) return '⇅';
    return this.sortDir === 'asc' ? '▲' : '▼';
  }

  /** Reset page when the user changes the search query. */
  onSearchInput(): void {
    this.currentPage = 0;
  }

  /** Clear the search input — bound to the × button inside the field. */
  clearSearch(): void {
    if (!this.searchText) return;
    this.searchText = '';
    this.currentPage = 0;
  }

  /** Switch the role filter (`null` clears it). */
  setRoleFilter(role: IAccount['ac_user_type_cod'] | null): void {
    this.roleFilter = role;
    this.currentPage = 0;
  }

  prevPage(): void {
    if (this.currentPage > 0) this.currentPage -= 1;
  }
  nextPage(): void {
    if (this.currentPage < this.totalPages - 1) this.currentPage += 1;
  }

  // ──────────────────────────────────────────────────────────────────
  // Actions — modal openers.
  // ──────────────────────────────────────────────────────────────────

  openNew(): void {
    this.selectedAccount = null;
    this.accountForm = this.buildForm();
    this.modalMode = 'new';
  }

  openView(account: IAccount): void {
    this.selectedAccount = account;
    this.modalMode = 'view';
  }

  openEdit(account: IAccount): void {
    this.selectedAccount = account;
    this.accountForm = this.buildForm(account);
    this.modalMode = 'edit';
  }

  openDelete(account: IAccount): void {
    this.selectedAccount = account;
    this.modalMode = 'delete';
  }

  closeModal(): void {
    this.modalMode = null;
    this.selectedAccount = null;
  }

  // ──────────────────────────────────────────────────────────────────
  // Actions — persistence.
  // ──────────────────────────────────────────────────────────────────

  /** Persist the form: handles both create and update. */
  save(): void {
    if (this.saving) return;
    if (this.accountForm.invalid) {
      this.accountForm.markAllAsTouched();
      return;
    }

    const payload = this.formToPayload();
    this.saving = true;

    const wasNew = payload.account_id === 0;

    this.accountsService
      .saveAccount(payload)
      .pipe(
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result: IWsResult) => {
          if (!result?.success) {
            Swal.fire({
              icon: 'error',
              title: 'Salvataggio fallito',
              text: 'Il backend ha rifiutato la richiesta. Riprova.',
              timer: 2200,
              timerProgressBar: true,
            });
            return;
          }
          // 🚨 FIX v2.12.0 (L.1.A): Swal di successo mancante. L'admin
          // non vedeva alcun feedback post-save → pensava "non funziona"
          // anche quando il save era andato a buon fine. Aggiunto popup
          // con messaggio diverso per create vs update.
          //
          // 🎨 v2.12.0 (L.1.C): theme Diakonix applicato globalmente via
          // src/theme/swal-diakonix.scss — niente customClass serve qui,
          // il SCSS targetta direttamente `.swal2-popup` / `.swal2-title`.
          // Usiamo `html:` con `<strong>` per evidenziare il nome account
          // in slate-900 weight 600 (regola `.swal2-html-container strong`).
          const fullName =
            `${payload.ac_first_name} ${payload.ac_last_name}`.trim();
          Swal.fire({
            icon: 'success',
            title: wasNew ? 'Account creato!' : 'Account aggiornato!',
            html: wasNew
              ? `<strong>${fullName}</strong> ora ha accesso a Diakonix.`
              : `Le modifiche a <strong>${fullName}</strong> sono state salvate.`,
            timer: 1800,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          this.closeModal();
          if (wasNew) {
            // New account → we need the server-assigned account_id, so
            // refetch the full list.
            this.loadAccounts();
          } else {
            // Update of an existing account → DO NOT refetch. The
            // backend's getAccounts filters out inactive (ac_active='N')
            // records, so a freshly-disabled account would disappear
            // from the list on the next load, leaving the admin without
            // a way to find it again. Patch the in-memory copy instead.
            // TODO: when the backend supports `?includeInactive=true`,
            // add a 'Mostra anche disattivati' toggle in the header and
            // restore the refetch.
            this.applyAccountUpdate(payload);
          }
        },
        error: (err) => {
          Log.error('accounts: save failed', err);
          Swal.fire({
            icon: 'error',
            title: 'Errore di rete',
            text: 'Salvataggio non completato. Riprova.',
            timer: 2200,
            timerProgressBar: true,
          });
        },
      });
  }

  /**
   * Trigger the password-reset flow for the account currently being edited.
   * Calls `AuthService.resetPassword(email)` which (per the backend
   * contract) sends a reset email / link to the account holder. The
   * admin never sees or sets the new password — this is by design.
   */
  triggerPasswordReset(): void {
    if (this.resettingPassword || !this.selectedAccount) return;
    const account = this.selectedAccount;
    const email = account.ac_email;

    Swal.fire({
      icon: 'question',
      title: 'Reset password',
      html: `Inviare il link di reset password a <strong>${email}</strong>?`,
      showCancelButton: true,
      confirmButtonText: 'Sì, invia',
      cancelButtonText: 'Annulla',
      confirmButtonColor: '#017efa',
    }).then((res) => {
      if (!res.isConfirmed) return;
      this.resettingPassword = true;
      // The backend `/reset_password/` endpoint expects `{ username }`
      // (see reset-password.component.ts — already calls it with the
      // form value of that shape). AuthService.resetPassword is typed
      // as `(username: string)` but in practice receives an object;
      // mismatch documented as a Phase 2 cleanup target.
      this.authService
        .resetPassword({ username: email } as unknown as string)
        .pipe(
          finalize(() => {
            this.resettingPassword = false;
            this.cdr.markForCheck();
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          next: (result: IWsResult) => {
            if (!result?.success) {
              Swal.fire({
                icon: 'error',
                title: 'Reset non inviato',
                text: 'Il backend ha rifiutato la richiesta.',
                timer: 2200,
                timerProgressBar: true,
              });
              return;
            }
            Swal.fire({
              icon: 'success',
              title: 'Email inviata',
              html: `Link di reset inviato a <strong>${email}</strong>.`,
              timer: 1800,
              timerProgressBar: true,
            });
          },
          error: (err) => {
            Log.error('accounts: resetPassword failed', err);
            Swal.fire({
              icon: 'error',
              title: 'Errore di rete',
              text: 'Reset non completato. Riprova.',
              timer: 2200,
              timerProgressBar: true,
            });
          },
        });
    });
  }

  /** Confirm deletion of `selectedAccount` (only callable from the delete modal). */
  confirmDelete(): void {
    if (this.deleting || !this.selectedAccount) return;
    const id = String(this.selectedAccount.account_id);
    this.deleting = true;

    this.accountsService
      .deleteAccount(id)
      .pipe(
        finalize(() => {
          this.deleting = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result: IWsResult) => {
          if (!result?.success) {
            Swal.fire({
              icon: 'error',
              title: 'Eliminazione fallita',
              text: 'Il backend ha rifiutato la richiesta.',
              timer: 2200,
              timerProgressBar: true,
            });
            return;
          }
          this.closeModal();
          this.loadAccounts();
        },
        error: (err) => {
          Log.error('accounts: delete failed', err);
          Swal.fire({
            icon: 'error',
            title: 'Errore di rete',
            text: 'Eliminazione non completata. Riprova.',
            timer: 2200,
            timerProgressBar: true,
          });
        },
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Private helpers.
  // ──────────────────────────────────────────────────────────────────

  /**
   * Replace an in-memory account record by `account_id`. Used after a
   * successful `saveAccount` UPDATE so the row remains visible (and
   * reflects the just-saved values) even if the backend would have
   * hidden it on the next list refresh (e.g. when ac_active was just
   * flipped to 'N').
   */
  private applyAccountUpdate(updated: IAccount): void {
    // Compare as strings: the backend sometimes returns numeric ids
    // as strings, and the form value goes through Number() before save.
    // A strict `===` between "5" and 5 would silently miss the match,
    // leaving the in-memory record stale and the just-disabled account
    // appearing to vanish at the next render. Coercing both sides to
    // string defeats the type mismatch.
    const idx = this.accounts.findIndex(
      (a) => String(a.account_id) === String(updated.account_id),
    );
    if (idx < 0) return;
    this.accounts = [
      ...this.accounts.slice(0, idx),
      { ...this.accounts[idx], ...updated },
      ...this.accounts.slice(idx + 1),
    ];
    this.cdr.markForCheck();
  }

  /** Fetch the full account list (used at boot + after every mutation). */
  private loadAccounts(): void {
    this.loading = true;
    this.accountsService
      .getAccounts()
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (data) => {
          this.accounts = data ?? [];
          this.currentPage = 0;
          // If the previous page is now beyond the new list length, clamp it.
          if (this.currentPage >= this.totalPages) {
            this.currentPage = this.totalPages - 1;
          }
          this.cdr.markForCheck();
          // v2.14.2 deep link: dopo che gli account sono caricati,
          // rispetta l'eventuale query-param `openAccountId` della
          // sidebar. Chiamato QUI (dentro next) perché serve la lista
          // popolata per matchare l'id → oggetto IAccount.
          this.tryOpenDeepLinkAccount();
        },
        error: (err) => Log.error('accounts: load failed', err),
      });
  }

  /**
   * Se l'URL contiene `?openAccountId=<id>`, apri il view modal per
   * quell'account e ripulisci il query param (one-shot).
   *
   * Silent-fail se:
   *   - il param non è un numero valido
   *   - l'id non matcha nessun account caricato (record eliminato o
   *     l'utente corrente non ha visibilità su quell'account)
   *
   * Il cleanup del param evita che un refresh browser ri-apra il
   * modale, che sarebbe UX confusa (l'utente si aspetta che refresh
   * resetti lo state UI).
   */
  private tryOpenDeepLinkAccount(): void {
    const raw = this.activatedRoute.snapshot.queryParamMap.get('openAccountId');
    if (!raw) return;
    const targetId = Number(raw);
    if (!Number.isFinite(targetId)) return;
    const match = this.accounts.find((a) => Number(a.account_id) === targetId);
    if (!match) return;
    this.openView(match);
    // Rimuovi il query param senza ricaricare la view (replaceUrl
    // così il tasto back non ha "step" fantasma nel history).
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { openAccountId: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /**
   * Build the reactive form — prefilled when editing.
   *
   * 🚨 FIX v2.12.0 (L.1.A): legacy non aveva il field `ac_password`
   * (commento al constructor diceva "admin doesn't set password from
   * this form, use reset flow"). MA per CREARE un nuovo account il BE
   * richiede una password iniziale → POST silenziosamente non creava
   * il record → loadAccounts rifaceva la GET con la stessa lista →
   * utente vedeva "non ha salvato". Il design del reset password resta
   * intatto per gli account ESISTENTI (admin invia reset email via
   * triggerPasswordReset), ma per la CREAZIONE l'admin deve fornire una
   * password iniziale che l'utente cambierà al primo login.
   * Il field è required solo per new (no account fornito).
   */
  private buildForm(account?: IAccount): FormGroup {
    const isNew = !account;
    return this.fb.group({
      account_id: [account?.account_id ?? 0],
      ac_first_name: [account?.ac_first_name ?? '', [Validators.required]],
      ac_last_name: [account?.ac_last_name ?? '', [Validators.required]],
      ac_email: [
        account?.ac_email ?? '',
        [Validators.required, Validators.email],
      ],
      ac_password: [
        '',
        isNew ? [Validators.required, Validators.minLength(6)] : [],
      ],
      ac_phone: [account?.ac_phone ?? ''],
      ac_mobile: [account?.ac_mobile ?? ''],
      ac_user_type_cod: [
        account?.ac_user_type_cod ?? 2 /* Operatore */,
        [Validators.required],
      ],
      ac_active: [this.coerceActive(account?.ac_active)],
      ac_expiration_date: [account?.ac_expiration_date ?? null],
      ac_note: [account?.ac_note ?? ''],
    });
  }

  /**
   * Convert form value → wire payload (boolean active → 'S'/'N').
   *
   * 🚨 FIX v2.12.0 (L.1.A): include `ac_password` SOLO per nuovi
   * account. Per gli update non lo mandiamo (admin non può cambiare
   * password altrui — usa reset flow).
   */
  private formToPayload(): IAccount {
    const v = this.accountForm.value;
    const accountId = Number(v.account_id) || 0;
    const isNew = accountId === 0;
    const payload: IAccount = {
      ...(this.selectedAccount ?? {}),
      account_id: accountId,
      ac_first_name: v.ac_first_name?.trim() ?? '',
      ac_last_name: v.ac_last_name?.trim() ?? '',
      ac_email: v.ac_email?.trim() ?? '',
      ac_phone: v.ac_phone ?? '',
      ac_mobile: v.ac_mobile ?? '',
      ac_user_type_cod: v.ac_user_type_cod,
      ac_active: v.ac_active ? 'S' : 'N',
      ac_expiration_date: v.ac_expiration_date ?? null,
      ac_note: v.ac_note ?? '',
    } as IAccount;
    if (isNew && v.ac_password) {
      (payload as IAccount & { ac_password: string }).ac_password =
        v.ac_password;
    }
    return payload;
  }

  /** Map backend 'S'/'N' (and stray boolean values) to a clean boolean. */
  private coerceActive(value: IAccount['ac_active'] | undefined): boolean {
    if (value === undefined || value === null) return true; // default active
    if (typeof value === 'boolean') return value;
    return value === 'S';
  }

  /** Build the search "haystack" for client-side filtering. */
  private haystack(account: IAccount): string {
    return [
      account.ac_first_name,
      account.ac_last_name,
      account.ac_email,
      account.ac_phone,
      account.ac_mobile,
      account.tab_user_type_desc,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }

  /** Code → RoleBadge mapping. Shared by the table cell and the dropdowns. */
  private badgeFromCode(code: IAccount['ac_user_type_cod']): RoleBadge {
    switch (code) {
      case 0:
        return { label: 'Super Admin', variant: 'super-admin' };
      case 1:
        return { label: 'Admin', variant: 'admin' };
      case 2:
        return { label: 'Operatore', variant: 'user' };
      case 3:
        return { label: 'Schermo TV', variant: 'screen' };
      case 4:
        return { label: 'Fornitore', variant: 'manufacturer' };
      case 'AGENT' as unknown as number:
        return { label: 'Agente', variant: 'agent' };
      default:
        return { label: '—', variant: 'none' };
    }
  }

  sortBy(column: string): void {
    const next = nextSortState(this.sortColumn, this.sortDirection, column);
    this.sortColumn = next.column;
    this.sortDirection = next.direction;
    this.cdr.detectChanges();
  }

  get sortedAccounts(): IAccount[] {
    if (!this.sortColumn) return this.filteredAccounts;

    return sortItems(
      this.filteredAccounts,
      this.sortColumn,
      this.sortDirection,
    );
  }
}
